/**
 * Declarative SOW calculations
 * ────────────────────────────
 * Derived SOW values ("30% of the full amount", "two months after go-live",
 * "thirty (30) days from the Effective Date", deposit amount, ...) are declared as
 * data, not hardcoded into the template prose. Each calculation is an expression
 * over the SOW data scope (the contract binding-expr language, incl. the date
 * functions in `bindings/expr.ts`). The evaluated outputs are exposed as
 * `calc.<key>` tokens/bindings - resolved everywhere the SOW renders, and
 * highlighted as computed content.
 */
import { z } from 'zod';
import { evaluateToString, validateExpressionSyntax } from './bindings/expr.js';
export const SOW_CALC_FORMATS = ['currency', 'date', 'percent', 'number', 'text'];
export const sowCalculationSchema = z.object({
    /** Output key; exposed as the `calc.<key>` token + binding. */
    key: z
        .string()
        .min(1)
        .regex(/^[a-z][a-z0-9_]*$/, 'calc key must be snake_case'),
    /** Human description (documentation / studio label). */
    label: z.string().optional(),
    /**
     * Expression over the SOW data scope (estimate totals, questionnaire dates,
     * config-with-defaults). Uses the contract binding-expr language incl. date
     * functions; produces the final display string (call `currency()`/`formatDate()`
     * in the expr as needed). Validated for syntax at load.
     */
    expr: z
        .string()
        .min(1)
        .superRefine((expr, ctx) => {
        try {
            validateExpressionSyntax(expr);
        }
        catch (e) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `invalid calc expr: ${e.message}`,
            });
        }
    }),
    /** Presentation hint (documentation; calc outputs are highlighted regardless). */
    format: z.enum(SOW_CALC_FORMATS).optional(),
});
export const sowCalculationsSchema = z.array(sowCalculationSchema);
/**
 * Evaluate calculations against a data scope -> `{ '<key>': '<display string>' }`.
 * A calc that throws at eval time falls back to '' so a missing input never breaks
 * generation/export (callers can `coalesce` a fallback inside the expr).
 */
export function evaluateSowCalculations(calcs, scope) {
    const out = {};
    for (const c of calcs) {
        try {
            out[c.key] = evaluateToString(c.expr, scope);
        }
        catch {
            out[c.key] = '';
        }
    }
    return out;
}
//# sourceMappingURL=sow-calculations.js.map